import createSagaMiddleware from 'redux-saga';
import combineSectionReducers from 'combine-section-reducers';
import {
  GlobalEventDispatcher,
  GlobalPut,
  integrateReduxDevtoolsExtension,
  NgRefreshStore,
  PS20Store,
} from '@ps20-frontend/shop-integration-redux';
import {
  gesamtbeitragMitRabattReducer,
  gesamtbeitragReducerForApp,
  jahresbeitragReducer,
  rabattstufeReducer,
} from '@ps20-frontend/reducers';
import { App } from '@ps20-frontend/frontend-models';
import { configureStore, Dispatch, Middleware, Tuple, UnknownAction } from '@reduxjs/toolkit';
import { rootSaga } from './sagas/rootSaga';
import { risikoortReducer } from './reducers/risikoort.reducer';
import { versicherungsnehmerReducer } from './reducers/versicherungsnehmer.reducer';
import { gebaeudeReducer } from './reducers/gebaeude.reducer';
import { merkmaleReducer } from './reducers/merkmale.reducer';
import { vorschaedenReducer } from './reducers/vorschaeden.reducer';
import { vwgVertragsoptionReducer } from './reducers/vertragsoptionen.reducer';
import { rootReducer } from './reducers/rootReducer.reducer';
import { INITIAL_STATE } from './initialState';
import { adressvorschlagReducer } from './reducers/adressvorschlag.reducer';
import { roleReducer } from './reducers/role.reducer';
import { IAppState } from './appState';
import { vorbelegungenReducer } from './reducers/vorbelegungen.reducer';
import { elementarSelbstbehalt } from './reducers/elementar-selbstbehalt.reducer';
import { plzRequiresUwReducer } from './reducers/plzRequiresUw.reducer';
import { vwgTaasInfoReducer } from './reducers/vwgTaasInfo.reducer';
import { transientStateReducer } from './reducers/transient-state.reducer';
import { vwgV2ProduktReducer } from './reducers/produkt.reducer';
import { gisDatenReducer } from './reducers/gisDaten.reducer';
import { antragsnummerReducer } from './reducers/antragsnummer.reducer';
import { risikoortActionHistory } from './risikoortActionHistory';
import { vnWechselAngabenReducer } from './reducers/vnWechselAngaben.reducer';
import { vorversicherungReducer } from './reducers/vorversicherung.reducer';
import { extremWetterschutzSelbstbeteiligungReducer } from './reducers/extremWetterschutzSelbstbeteiligung.reducer';
import { erdbebenSelbstbetailigungReducer } from './reducers/erdbebenSelbstbeteiligung.reducer';
import { ueberschwemmungSelbstbeteiligungReducer } from './reducers/ueberschwemmungsSelbstbeteiligung.reducer';

export const reducers = combineSectionReducers({
  version: (version: number = null) => version,
  antragsnummer: antragsnummerReducer,
  vorbelegungen: vorbelegungenReducer,
  risikoort: risikoortReducer,
  adressvorschlag: adressvorschlagReducer,
  gebaeude: gebaeudeReducer,
  buildingFeatures: merkmaleReducer,
  vorschaeden: vorschaedenReducer,
  produkt: vwgV2ProduktReducer,
  vertragsoptionen: vwgVertragsoptionReducer,
  jahresbeitrag: jahresbeitragReducer,
  gesamtbeitrag: gesamtbeitragReducerForApp(App.VWG_V2),
  gesamtbeitragMitRabatt: gesamtbeitragMitRabattReducer,
  taasInfo: vwgTaasInfoReducer,
  versicherungsnehmer: versicherungsnehmerReducer,
  transientState: transientStateReducer,
  role: roleReducer,
  gisDaten: gisDatenReducer,
  elementarSelbstbehalt,
  plzRequiresUw: plzRequiresUwReducer,
  rabattstufe: rabattstufeReducer,
  vnWechselAngaben: vnWechselAngabenReducer,
  hasVorversicherung: vorversicherungReducer,
  extremeWetterschutzSelbstbeteiligung: extremWetterschutzSelbstbeteiligungReducer,
  ueberschwemmungSelbstbeteiligung: ueberschwemmungSelbstbeteiligungReducer,
  erdbebenSelbstbetailigung: erdbebenSelbstbetailigungReducer,
});

export function vwgV2StoreFactory(storeIndex: number): PS20Store<IAppState> {
  const ngRefreshStore = new NgRefreshStore<IAppState>(App.VWG_V2);
  const sagaMiddleware = createSagaMiddleware();

  const store = configureStore({
    reducer: rootReducer(reducers),
    devTools: integrateReduxDevtoolsExtension(`vwg-angebot-v2-gui-${storeIndex}`),
    preloadedState: INITIAL_STATE,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(sagaMiddleware, ngRefreshStore.ngRefreshMiddleware, risikoortActionHistory.middleware) as unknown as Tuple<ReadonlyArray<Middleware<Record<string, never>, IAppState, Dispatch<UnknownAction>>>>,
  });

  sagaMiddleware.run(rootSaga);
  ngRefreshStore.setDelegate(store);
  return ngRefreshStore;
}

GlobalPut.getInstance().globalEventDispatcherFunction = () => {
  return (window as unknown as Record<string, unknown>)['globalEventDispatcher'] as GlobalEventDispatcher;
};
